<!--
  SPEC 38 §1.4: ONE WORK ORDER, created, edited, completed, with its invoice.
  The same dialog from every door into a work order: the truck's Maintenance
  page (by hand, or "Create work order" on a PM schedule), the fleet board,
  and "Create work order" on a vehicle fault in the alert bell and the live
  map's feed. Each door only says where the work came from (`prefill`); what a
  work order IS stays in one place.

  CREATE sends only what was typed: no empty vendor, no lines that are all
  blank, no odometer when none was given. `out_of_service: { reason }` (the
  API's name for the company's own hold) rides along when "Take the unit off
  the road (hold) until this is done" is ticked; the server opens that hold in
  the same transaction and completing the work order releases it. The screen
  never says "out of service": that is a roadside order under 49 CFR
  396.9(c), not something the carrier's office does.

  EDIT sends only the fields that changed, like the credentials dialog, and
  lines as a whole list when any of them changed (the API replaces lines; a
  partial list would delete the rest).

  THE LINE AMOUNTS AND THE TOTAL SHOWN WHILE TYPING ARE A PREVIEW. The server
  computes every amount (quantity x unit cost, to the cent) and the total, and
  never takes them from the browser; once saved, the figures shown are the
  server's.

  COMPLETE is a step of its own, not a status in the select: it does three
  things the select cannot undo (spec 38 §1.4). Every open inspection-report
  defect the work order holds is certified -- the carrier's repair
  certification on the report (49 CFR 396.11(a)(3)(ii)) -- so each defect
  gets its own "Repaired / Repair not needed" choice, and NOTHING IS CHOSEN
  FOR THEM: each starts unanswered and Mark done stays disabled until every
  one is answered (the server refuses a missing one with a 400 too). Who
  certifies is chosen as well (our office, or a named repair facility,
  prefilled with the vendor), and the fixed statement that will be recorded
  is shown as a preview beside each choice; the server renders and stores its
  own copy. The holds it opened are released; and a PM work order resets its
  schedule. Unsaved edits are saved first, then the completion is sent.
  Done is final: a done work order opens read-only, apart from its invoice,
  which often arrives after the truck has left the shop.

  Reading is fleet.view; every input and button that writes is fleet.manage.

  data-tour="work-order" (spec 53, tours/definitions/defect-to-work-order.js),
  keyed by the work order's id once it has loaded and `completing` while the
  Mark done step shows: the walkthrough waits on each.
-->
<template>
  <LwDialog
    :open="open"
    :title="dialogTitle"
    :description="dialogDescription"
    data-tour="work-order"
    :data-tour-key="row ? (completing ? 'completing' : String(row.id)) : null"
    size="wide"
    :busy="saving"
    @confirm="primaryAction"
    @cancel="close"
  >
    <div class="lw-c-work-order" data-test="work-order-dialog">
      <p v-if="loading" class="lw-c-work-order__status">Loading the work order…</p>
      <LwNotice v-else-if="loadError" tone="danger">
        {{ loadError }}
        <template #actions>
          <LwButton size="sm" variant="secondary" @click="fetchRow">Retry</LwButton>
        </template>
      </LwNotice>

      <template v-else>
        <LwNotice v-if="notice" :tone="noticeTone" dismissible @dismiss="notice = null">
          {{ notice }}
        </LwNotice>

        <!-- Where it came from, and its state, for an existing work order. -->
        <dl v-if="row" class="lw-c-work-order__facts">
          <div>
            <dt>Status</dt>
            <dd>
              <LwChip
                axis="feedback"
                :value="statusChip.value"
                :label="statusChip.label"
                data-test="work-order-status-chip"
              />
            </dd>
          </div>
          <div>
            <dt>From</dt>
            <dd>
              {{ openedFromLabel(row.opened_from)
              }}<template v-if="row.pm_schedule_name"> · {{ row.pm_schedule_name }}</template>
            </dd>
          </div>
          <div>
            <dt>Opened</dt>
            <dd>{{ when(row.created_at) }}</dd>
          </div>
          <div v-if="row.completed_at">
            <dt>Completed</dt>
            <dd>{{ when(row.completed_at) }}</dd>
          </div>
        </dl>

        <!-- ------------------------------------------------ read-only -->
        <template v-if="readOnly">
          <p class="lw-c-work-order__text" data-test="work-order-description">
            {{ row && row.description }}
          </p>
          <p v-if="row && row.vendor" class="lw-c-work-order__meta">Vendor: {{ row.vendor }}</p>
          <p v-if="row && row.odometer_value != null" class="lw-c-work-order__meta">
            Odometer at service: {{ formatDistance(row.odometer_value, row.odometer_unit) }}
          </p>
          <p v-if="row && row.completion_note" class="lw-c-work-order__meta">
            Note: {{ row.completion_note }}
          </p>
          <table v-if="row && row.lines && row.lines.length" class="lw-c-work-order__lines-table">
            <caption class="lw-c-work-order__sr">
              Work order lines
            </caption>
            <thead>
              <tr>
                <th scope="col">Kind</th>
                <th scope="col">Description</th>
                <th scope="col">Qty</th>
                <th scope="col">Unit cost</th>
                <th scope="col">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in row.lines" :key="line.id">
                <td>{{ kindLabel(line.kind) }}</td>
                <td>{{ line.description }}</td>
                <td>{{ line.quantity }}</td>
                <td>{{ money(line.unit_cost) }}</td>
                <td>{{ money(line.amount) }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="row" class="lw-c-work-order__total" data-test="work-order-total">
            Total {{ money(row.total) }}
          </p>
        </template>

        <!-- ------------------------------------------------- the form -->
        <template v-else>
          <LwField
            v-model="form.description"
            type="textarea"
            :rows="2"
            label="What needs doing"
            required
            :error="errors.description"
            data-test="work-order-description"
          />
          <div class="lw-c-work-order__row">
            <LwField
              v-model="form.vendor"
              label="Vendor (optional)"
              :maxlength="191"
              :error="errors.vendor"
              data-test="work-order-vendor"
            />
            <LwField
              v-if="row"
              v-model="form.status"
              type="select"
              label="Status"
              :options="statusOptions"
              :error="errors.status"
              data-test="work-order-status"
            />
          </div>
          <div class="lw-c-work-order__row">
            <LwField
              v-model="form.odometer_value"
              type="number"
              inputmode="decimal"
              label="Odometer at service (optional)"
              :error="errors.odometer_value"
              data-test="work-order-odometer"
            />
            <LwField
              v-model="form.odometer_unit"
              type="select"
              label="Unit"
              :options="unitOptions"
              data-test="work-order-odometer-unit"
            />
          </div>

          <!-- Lines: parts, labour, other. -->
          <fieldset class="lw-c-work-order__lines">
            <legend class="lw-c-work-order__legend">Lines</legend>
            <p v-if="!form.lines.length" class="lw-c-work-order__meta">
              No lines yet. Add parts and labour as the shop quotes or bills them.
            </p>
            <div
              v-for="(line, i) in form.lines"
              :key="line.key"
              class="lw-c-work-order__line"
              :data-test="`work-order-line-${i}`"
            >
              <LwField
                v-model="line.kind"
                type="select"
                label="Kind"
                :options="lineKinds"
                :data-test="`work-order-line-kind-${i}`"
              />
              <LwField
                v-model="line.description"
                label="Description"
                :maxlength="500"
                :data-test="`work-order-line-description-${i}`"
              />
              <LwField
                v-model="line.quantity"
                type="number"
                inputmode="decimal"
                label="Qty"
                :data-test="`work-order-line-quantity-${i}`"
              />
              <LwField
                v-model="line.unit_cost"
                type="number"
                inputmode="decimal"
                label="Unit cost"
                :data-test="`work-order-line-cost-${i}`"
              />
              <p class="lw-c-work-order__amount" :data-test="`work-order-line-amount-${i}`">
                {{ money(lineAmount(line)) }}
              </p>
              <LwButton
                size="sm"
                variant="ghost"
                :aria-label="`Remove line ${i + 1}`"
                :data-test="`work-order-line-remove-${i}`"
                @click="removeLine(i)"
              >
                Remove
              </LwButton>
              <p v-if="errors[`lines.${i}`]" class="lw-c-work-order__error" role="alert">
                {{ errors[`lines.${i}`] }}
              </p>
            </div>
            <div class="lw-c-work-order__lines-foot">
              <LwButton
                size="sm"
                variant="secondary"
                data-test="work-order-add-line"
                @click="addLine"
              >
                Add line
              </LwButton>
              <p class="lw-c-work-order__total" data-test="work-order-total">
                Total {{ money(previewTotal) }}
                <span class="lw-c-work-order__meta">(worked out again when saved)</span>
              </p>
            </div>
            <p v-if="errors.lines" class="lw-c-work-order__error" role="alert">
              {{ errors.lines }}
            </p>
          </fieldset>

          <!-- Create only: hold the unit (take it off the road) with it. -->
          <div v-if="!row" class="lw-c-work-order__oos">
            <label class="lw-c-work-order__check">
              <input v-model="form.oos" type="checkbox" data-test="work-order-oos" />
              Take the unit off the road (hold) until this is done
            </label>
            <LwField
              v-if="form.oos"
              v-model="form.oosReason"
              label="Why it is held"
              required
              :error="errors.out_of_service"
              data-test="work-order-oos-reason"
            />
          </div>
        </template>

        <!-- The defects this work order holds (an inspection report's). -->
        <section v-if="row && defects.length" class="lw-c-work-order__defects">
          <h3 class="lw-c-work-order__heading">Inspection report defects</h3>
          <ul class="lw-c-work-order__defect-list">
            <li
              v-for="d in defects"
              :key="d.id"
              class="lw-c-work-order__defect"
              :data-test="`work-order-defect-${d.id}`"
            >
              <span class="lw-c-work-order__defect-area">{{ d.area_label }}</span>
              <span v-if="d.note" class="lw-c-work-order__meta">{{ d.note }}</span>
              <span v-if="d.reported_by" class="lw-c-work-order__meta">{{
                reportedByText(d)
              }}</span>
              <template v-if="d.status !== 'open'">
                <span class="lw-c-work-order__meta" :data-test="`work-order-defect-cert-${d.id}`">
                  {{ certificationSummary(d) }}
                </span>
                <span
                  v-if="d.certification_statement"
                  class="lw-c-work-order__statement"
                  :data-test="`work-order-defect-statement-${d.id}`"
                  >“{{ d.certification_statement }}”</span
                >
              </template>
              <fieldset
                v-else-if="completing"
                class="lw-c-work-order__choice"
                :data-test="`work-order-certify-${d.id}`"
              >
                <legend class="lw-c-work-order__sr">
                  Repair certification for {{ d.area_label }}
                </legend>
                <label
                  v-for="c in certificationOptions"
                  :key="c.value"
                  class="lw-c-work-order__check"
                >
                  <input
                    v-model="complete.certifications[String(d.id)]"
                    type="radio"
                    :name="`work-order-certify-${d.id}`"
                    :value="c.value"
                    :data-test="`work-order-certify-${d.id}-${c.value}`"
                  />
                  {{ c.label }}
                </label>
                <span
                  v-if="complete.certifications[String(d.id)]"
                  class="lw-c-work-order__statement"
                  :data-test="`work-order-certify-statement-${d.id}`"
                  >Recorded: “{{ statementFor(complete.certifications[String(d.id)]) }}”</span
                >
                <span v-else class="lw-c-work-order__meta">Not answered yet.</span>
              </fieldset>
              <span v-else class="lw-c-work-order__meta"
                >Repair not yet certified. Certified when this work order is done.</span
              >
            </li>
          </ul>
        </section>

        <!-- The completion step. -->
        <section
          v-if="completing"
          class="lw-c-work-order__complete"
          data-test="work-order-complete"
        >
          <h3 class="lw-c-work-order__heading">Mark done</h3>
          <p class="lw-c-work-order__meta">
            {{ completeExplainer }}
          </p>
          <fieldset
            v-if="openDefects.length"
            class="lw-c-work-order__choice"
            data-test="work-order-certifier"
          >
            <legend class="lw-c-work-order__legend">Certified by</legend>
            <label v-for="k in certifierKinds" :key="k.value" class="lw-c-work-order__check">
              <input
                v-model="complete.certifier_kind"
                type="radio"
                name="work-order-certifier-kind"
                :value="k.value"
                :data-test="`work-order-certifier-${k.value}`"
              />
              {{ k.label }}
            </label>
            <LwField
              v-if="complete.certifier_kind === 'repair_facility'"
              v-model="complete.certifier_org_name"
              label="Repair facility name"
              required
              :maxlength="255"
              :error="errors.certifier_org_name"
              data-test="work-order-certifier-org"
            />
            <span class="lw-c-work-order__meta">
              Recorded with your name and the time, and the statement shown beside each defect.
            </span>
          </fieldset>
          <div class="lw-c-work-order__row">
            <LwField
              v-model="complete.completed_on"
              type="date"
              label="Completed on"
              description="Today when left empty."
              :error="errors.completed_on"
              data-test="work-order-completed-on"
            />
            <LwField
              v-model="complete.odometer_value"
              type="number"
              inputmode="decimal"
              label="Odometer at service"
              :error="errors.odometer_value"
              data-test="work-order-complete-odometer"
            />
            <LwField
              v-model="complete.odometer_unit"
              type="select"
              label="Unit"
              :options="unitOptions"
              data-test="work-order-complete-odometer-unit"
            />
          </div>
          <LwField
            v-model="complete.note"
            type="textarea"
            :rows="2"
            label="Note (optional)"
            data-test="work-order-complete-note"
          />
        </section>

        <!-- The shop's invoice. -->
        <section v-if="row || canManage" class="lw-c-work-order__invoice">
          <h3 class="lw-c-work-order__heading">Invoice</h3>
          <a
            v-if="row && row.has_invoice"
            :href="invoiceHref(row.id)"
            target="_blank"
            rel="noopener"
            class="lw-c-work-order__link"
            data-test="work-order-invoice-link"
            >View invoice</a
          >
          <span v-else-if="row" class="lw-c-work-order__meta">None on file.</span>
          <LwField
            v-if="canManage"
            :label="row && row.has_invoice ? 'Replace the invoice' : 'Attach the invoice'"
            description="A PDF or a photo, up to 10 MB."
            :error="fileError"
          >
            <template #default="{ id, describedby }">
              <input
                :id="id"
                type="file"
                class="lw-c-work-order__file"
                :accept="accept"
                :aria-describedby="describedby"
                :disabled="uploading"
                data-test="work-order-invoice-file"
                @change="fileChosen"
              />
            </template>
          </LwField>
        </section>

        <p
          v-if="completing && !allAnswered"
          class="lw-c-work-order__meta"
          data-test="work-order-unanswered"
        >
          Choose Repaired or Repair not needed for each defect to mark it done.
        </p>
        <LwNotice v-if="message" tone="danger">{{ message }}</LwNotice>
      </template>
    </div>

    <template #actions>
      <LwButton variant="secondary" data-test="work-order-close" @click="close">
        {{ readOnly ? 'Close' : 'Cancel' }}
      </LwButton>
      <template v-if="canManage && !readOnly && !loading && !loadError">
        <LwButton
          v-if="row && !completing"
          variant="secondary"
          data-test="work-order-start-complete"
          @click="startComplete"
        >
          Complete…
        </LwButton>
        <LwButton
          v-if="completing"
          variant="secondary"
          data-test="work-order-back"
          @click="completing = false"
        >
          Back
        </LwButton>
        <LwButton
          variant="primary"
          :loading="saving"
          :disabled="completing && !allAnswered"
          data-test="work-order-confirm"
          @click="primaryAction"
        >
          {{ confirmLabel }}
        </LwButton>
      </template>
    </template>
  </LwDialog>
</template>

<script>
import LwButton from '../lw/LwButton.vue';
import LwChip from '../lw/LwChip.vue';
import LwDialog from '../lw/LwDialog.vue';
import LwField from '../lw/LwField.vue';
import LwNotice from '../lw/LwNotice.vue';
import { mapResponseError } from '../../lib/formErrors';
import { orgSetting } from '../../lib/orgSettings';
import { orgZone } from '../../lib/orgTime';
import { canManageFleet, DOCUMENT_ACCEPT, documentFileProblem, uploadProblem } from './fleetFormat';
import {
  carrierNameOf,
  CERTIFICATIONS,
  CERTIFIER_KINDS,
  certificationStatement,
  certificationSummary,
  formatDistance,
  formatInstant,
  formatMoney,
  invoiceHref,
  LINE_KINDS,
  lineAmount,
  linesTotal,
  maintenanceProblem,
  openedFromLabel,
  reportedByText,
  workOrderStatusChip,
} from './maintenanceFormat';

let lineSeq = 0;
function blankLine(over = {}) {
  lineSeq += 1;
  return {
    key: `l${lineSeq}`,
    kind: 'part',
    description: '',
    quantity: '1',
    unit_cost: '',
    ...over,
  };
}

function blankForm() {
  return {
    description: '',
    vendor: '',
    status: 'open',
    odometer_value: '',
    odometer_unit: 'mi',
    lines: [],
    oos: false,
    oosReason: '',
  };
}

function blankComplete() {
  return {
    completed_on: '',
    odometer_value: '',
    odometer_unit: 'mi',
    note: '',
    // One explicit choice per open defect; none is made for the user.
    certifications: {},
    certifier_kind: 'office',
    certifier_org_name: '',
  };
}

const CERTIFICATION_VALUES = CERTIFICATIONS.map(c => c.value);

const text = v => (v == null ? '' : String(v).trim());

/** The lines as the API takes them; wholly blank lines are dropped. */
function cleanLines(lines) {
  return (lines || [])
    .filter(l => text(l.description) || text(l.unit_cost))
    .map(l => ({
      kind: l.kind,
      description: text(l.description),
      quantity: text(l.quantity) === '' ? 1 : Number(l.quantity),
      unit_cost: text(l.unit_cost) === '' ? 0 : Number(l.unit_cost),
    }));
}

const FIELDS = [
  'description',
  'vendor',
  'status',
  'odometer_value',
  'out_of_service',
  'lines',
  'certifier_org_name',
];

export default {
  name: 'WorkOrderDialog',
  components: { LwButton, LwChip, LwDialog, LwField, LwNotice },
  props: {
    open: { type: Boolean, default: false },
    // An existing work order to open; null to create one.
    workOrderId: { type: [String, Number], default: null },
    // The unit a new work order is for (a fault's or a schedule's unit is
    // found by the server when this is not known).
    truckId: { type: [String, Number], default: null },
    // "Unit 214", for the title.
    unitLabel: { type: String, default: null },
    // Where a new one came from: { opened_from, pm_schedule_id,
    // vehicle_event_id, description }.
    prefill: { type: Object, default: null },
  },
  // saved: { row, message } after a create, save or completion; the caller
  // closes and re-reads. changed: an invoice went up on an open dialog.
  emits: ['close', 'saved', 'changed'],
  data() {
    return {
      row: null,
      loading: false,
      loadError: null,
      form: blankForm(),
      complete: blankComplete(),
      completing: false,
      errors: {},
      message: null,
      notice: null,
      noticeTone: 'success',
      saving: false,
      uploading: false,
      file: null,
      fileError: null,
      accept: DOCUMENT_ACCEPT,
      lineKinds: LINE_KINDS,
      certificationOptions: CERTIFICATIONS,
      certifierKinds: CERTIFIER_KINDS,
      unitOptions: [
        { value: 'mi', label: 'mi' },
        { value: 'km', label: 'km' },
      ],
      statusOptions: [
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In progress' },
      ],
    };
  },
  computed: {
    canManage() {
      return canManageFleet(this.$store);
    },
    zone() {
      return orgZone(this.$store);
    },
    currency() {
      return orgSetting(this.$store, 'org.currency', 'USD');
    },
    isDone() {
      return !!this.row && this.row.status === 'done';
    },
    readOnly() {
      return !this.canManage || this.isDone;
    },
    statusChip() {
      return workOrderStatusChip(this.row && this.row.status);
    },
    defects() {
      return (this.row && Array.isArray(this.row.defects) && this.row.defects) || [];
    },
    openDefects() {
      return this.defects.filter(d => d.status === 'open');
    },
    // Every open defect has an explicit Repaired / Repair not needed.
    allAnswered() {
      return this.openDefects.every(d =>
        CERTIFICATION_VALUES.includes(this.complete.certifications[String(d.id)])
      );
    },
    carrierName() {
      return carrierNameOf(this.$store);
    },
    previewTotal() {
      return linesTotal(this.form.lines);
    },
    dialogTitle() {
      const unit =
        this.unitLabel || (this.row && this.row.unit_number && `Unit ${this.row.unit_number}`);
      if (this.workOrderId == null) return unit ? `New work order for ${unit}` : 'New work order';
      return unit ? `Work order ${this.workOrderId} · ${unit}` : `Work order ${this.workOrderId}`;
    },
    dialogDescription() {
      if (this.workOrderId != null) return null;
      const from = this.prefill && this.prefill.opened_from;
      if (from === 'pm') return 'Completing it resets the PM schedule.';
      if (from === 'fault_event')
        return 'For the vehicle fault, which stays on the alert list until it is cleared.';
      return null;
    },
    confirmLabel() {
      if (this.completing) return 'Mark done';
      return this.row ? 'Save' : 'Create work order';
    },
    completeExplainer() {
      const bits = [];
      if (this.openDefects.length) {
        bits.push(
          'Each inspection report defect below is certified as you choose, with your name and the time.'
        );
      }
      bits.push('Any hold this work order put on the unit is released.');
      if (this.row && this.row.pm_schedule_id) {
        bits.push('Its PM schedule is reset to this date and odometer.');
      }
      bits.push('A done work order cannot be edited.');
      return bits.join(' ');
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(now) {
        if (now) this.reset();
      },
    },
  },
  methods: {
    openedFromLabel,
    reportedByText,
    formatDistance,
    invoiceHref,
    lineAmount,
    money(n) {
      return formatMoney(n, this.currency);
    },
    when(iso) {
      return formatInstant(iso, this.zone);
    },
    certificationSummary(d) {
      return certificationSummary(d, this.zone);
    },
    statementFor(certification) {
      return certificationStatement(certification, this.carrierName);
    },
    kindLabel(kind) {
      const found = LINE_KINDS.find(k => k.value === kind);
      return found ? found.label : kind;
    },
    reset() {
      this.row = null;
      this.loadError = null;
      this.errors = {};
      this.message = null;
      this.notice = null;
      this.completing = false;
      this.saving = false;
      this.file = null;
      this.fileError = null;
      this.complete = blankComplete();
      this.form = blankForm();
      if (this.workOrderId != null) return this.fetchRow();
      const p = this.prefill || {};
      if (p.description) this.form.description = String(p.description);
      return undefined;
    },
    fetchRow() {
      this.loading = true;
      this.loadError = null;
      return Promise.resolve(this.$http.get(`/fleet/work-orders/${this.workOrderId}`))
        .then(res => {
          this.setRow((res && res.data) || null);
        })
        .catch(() => {
          this.loadError = 'The work order could not be loaded.';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setRow(row) {
      this.row = row;
      if (!row) return;
      this.form = {
        ...blankForm(),
        description: row.description || '',
        vendor: row.vendor || '',
        status: row.status === 'in_progress' ? 'in_progress' : 'open',
        odometer_value: row.odometer_value == null ? '' : String(row.odometer_value),
        odometer_unit: row.odometer_unit || 'mi',
        lines: (row.lines || []).map(l =>
          blankLine({
            kind: l.kind,
            description: l.description || '',
            quantity: l.quantity == null ? '' : String(l.quantity),
            unit_cost: l.unit_cost == null ? '' : String(l.unit_cost),
          })
        ),
      };
    },
    close() {
      this.$emit('close');
    },
    addLine() {
      this.form.lines.push(blankLine());
    },
    removeLine(i) {
      this.form.lines.splice(i, 1);
    },
    startComplete() {
      // Unanswered on purpose: no defect is certified unless someone says so.
      this.complete = {
        ...blankComplete(),
        odometer_value: this.form.odometer_value,
        odometer_unit: this.form.odometer_unit || 'mi',
        certifier_org_name: text(this.form.vendor),
      };
      this.errors = {};
      this.message = null;
      this.completing = true;
    },
    primaryAction() {
      if (this.readOnly || this.loading || this.loadError) return this.close();
      if (this.completing) return this.markDone();
      return this.save();
    },

    // ------------------------------------------------------------ bodies
    createBody() {
      const f = this.form;
      const p = this.prefill || {};
      const body = { opened_from: p.opened_from || 'manual', description: text(f.description) };
      if (this.truckId != null) body.truck_id = String(this.truckId);
      if (body.opened_from === 'pm' && p.pm_schedule_id != null) {
        body.pm_schedule_id = Number(p.pm_schedule_id);
      }
      if (body.opened_from === 'fault_event' && p.vehicle_event_id != null) {
        body.vehicle_event_id = String(p.vehicle_event_id);
      }
      if (text(f.vendor)) body.vendor = text(f.vendor);
      const lines = cleanLines(f.lines);
      if (lines.length) body.lines = lines;
      if (text(f.odometer_value)) {
        body.odometer_value = Number(f.odometer_value);
        body.odometer_unit = f.odometer_unit || 'mi';
      }
      if (f.oos) body.out_of_service = { reason: text(f.oosReason) };
      return body;
    },
    editBody() {
      const f = this.form;
      const o = this.row || {};
      const body = {};
      if (text(f.description) !== text(o.description)) body.description = text(f.description);
      if (text(f.vendor) !== text(o.vendor)) body.vendor = text(f.vendor) || null;
      if (f.status !== o.status) body.status = f.status;
      const before = cleanLines(
        (o.lines || []).map(l => ({
          ...l,
          quantity: String(l.quantity),
          unit_cost: String(l.unit_cost),
        }))
      );
      const after = cleanLines(f.lines);
      if (JSON.stringify(before) !== JSON.stringify(after)) body.lines = after;
      const odoBefore = o.odometer_value == null ? '' : String(o.odometer_value);
      if (
        text(f.odometer_value) !== text(odoBefore) ||
        (text(f.odometer_value) && f.odometer_unit !== (o.odometer_unit || 'mi'))
      ) {
        body.odometer_value = text(f.odometer_value) ? Number(f.odometer_value) : null;
        if (body.odometer_value != null) body.odometer_unit = f.odometer_unit || 'mi';
      }
      return body;
    },
    completeBody() {
      const c = this.complete;
      const body = {};
      if (c.completed_on) body.completed_on = c.completed_on;
      if (text(c.odometer_value)) {
        body.odometer_value = Number(c.odometer_value);
        body.odometer_unit = c.odometer_unit || 'mi';
      }
      if (text(c.note)) body.note = text(c.note);
      if (this.openDefects.length) {
        const certifications = {};
        this.openDefects.forEach(d => {
          certifications[String(d.id)] = c.certifications[String(d.id)];
        });
        body.certifications = certifications;
        body.certifier_kind = c.certifier_kind;
        if (c.certifier_kind === 'repair_facility') {
          body.certifier_org_name = text(c.certifier_org_name);
        }
      }
      return body;
    },

    // ------------------------------------------------------------ writes
    validate() {
      const errors = {};
      if (!text(this.form.description)) errors.description = 'Say what the work is.';
      if (!this.row && this.form.oos && !text(this.form.oosReason)) {
        errors.out_of_service = 'Say why the unit is held.';
      }
      const file = this.file;
      if (file && documentFileProblem(file)) this.fileError = documentFileProblem(file);
      this.errors = errors;
      return !Object.keys(errors).length && !(file && this.fileError);
    },
    failed(err) {
      const res = err && err.response;
      const lineKeys = Object.keys((res && res.data && res.data.errors) || {}).filter(k =>
        /^lines\.\d+$/.test(k)
      );
      const { fields, message } = mapResponseError(res, [...FIELDS, ...lineKeys, 'completed_on']);
      this.errors = fields || {};
      if (res && res.status === 409) {
        this.message = 'This work order is already done. Close this and reload to see it.';
      } else if (res && res.status === 404) {
        this.message = maintenanceProblem(err, message);
      } else {
        this.message = message;
      }
    },
    save() {
      if (this.saving) return undefined;
      this.message = null;
      if (!this.validate()) return undefined;
      const creating = !this.row;
      const body = creating ? this.createBody() : this.editBody();
      this.saving = true;
      const request = creating
        ? this.$http.post('/fleet/work-orders', body)
        : Object.keys(body).length
          ? this.$http.patch(`/fleet/work-orders/${this.row.id}`, body)
          : Promise.resolve({ data: this.row });
      return Promise.resolve(request)
        .then(res => {
          const saved = (res && res.data) || {};
          const what = creating
            ? `Work order ${saved.id || ''} created`
            : `Work order ${saved.id || this.row.id} saved`;
          const file = this.file;
          if (!file || saved.id == null) {
            this.$emit('saved', { row: saved, message: `${what.trim()}.` });
            return undefined;
          }
          return this.uploadInvoice(saved.id, file)
            .then(() => this.$emit('saved', { row: saved, message: `${what}, with its invoice.` }))
            .catch(err =>
              this.$emit('saved', {
                row: saved,
                message: `${what}, but the invoice was not: ${uploadProblem(err)}`,
                tone: 'warning',
              })
            );
        })
        .catch(err => this.failed(err))
        .finally(() => {
          this.saving = false;
        });
    },
    markDone() {
      if (this.saving || !this.row) return undefined;
      this.message = null;
      if (!this.validate()) return undefined;
      if (!this.allAnswered) {
        this.message = 'Choose Repaired or Repair not needed for each defect to mark it done.';
        return undefined;
      }
      if (
        this.openDefects.length &&
        this.complete.certifier_kind === 'repair_facility' &&
        !text(this.complete.certifier_org_name)
      ) {
        this.errors = { certifier_org_name: 'Name the repair facility that certifies it.' };
        return undefined;
      }
      const id = this.row.id;
      const edits = this.editBody();
      const completion = this.completeBody();
      this.saving = true;
      const first = Object.keys(edits).length
        ? this.$http.patch(`/fleet/work-orders/${id}`, edits)
        : Promise.resolve(null);
      return Promise.resolve(first)
        .then(() => this.$http.post(`/fleet/work-orders/${id}/complete`, completion))
        .then(res => {
          const done = (res && res.data) || {};
          const certified = (done.defects || []).length;
          const tail = certified
            ? ` ${certified} inspection report defect${certified === 1 ? '' : 's'} certified.`
            : '';
          this.$emit('saved', { row: done, message: `Work order ${id} is done.${tail}` });
        })
        .catch(err => this.failed(err))
        .finally(() => {
          this.saving = false;
        });
    },

    // ----------------------------------------------------------- invoice
    uploadInvoice(id, file) {
      const data = new FormData();
      data.append('file', file);
      return Promise.resolve(this.$http.post(`/fleet/work-orders/${id}/invoice`, data));
    },
    fileChosen(event) {
      const file = event && event.target && event.target.files ? event.target.files[0] : null;
      this.file = file || null;
      this.fileError = file ? documentFileProblem(file) : null;
      // An existing work order takes its invoice now; a new one after it is
      // created (it needs the id for the storage path).
      if (!file || this.fileError || !this.row) return undefined;
      this.uploading = true;
      return this.uploadInvoice(this.row.id, file)
        .then(res => {
          if (res && res.data && res.data.id != null) this.row = { ...this.row, ...res.data };
          else this.row = { ...this.row, has_invoice: true };
          this.file = null;
          this.notice = 'Invoice saved.';
          this.noticeTone = 'success';
          this.$emit('changed');
        })
        .catch(err => {
          this.fileError = uploadProblem(err);
        })
        .finally(() => {
          this.uploading = false;
        });
    },
  },
};
</script>

<style scoped lang="scss">
.lw-c-work-order {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
}

.lw-c-work-order__status,
.lw-c-work-order__text {
  margin: 0;
  font: var(--lw-type-body);
}

.lw-c-work-order__meta {
  display: block;
  margin: 0;
  font: var(--lw-type-caption);
  color: var(--lw-fg-secondary);
}

.lw-c-work-order__facts {
  display: flex;
  flex-wrap: wrap;
  gap: var(--lw-space-2) var(--lw-space-5);
  margin: 0;

  dt {
    font: var(--lw-type-caption);
    color: var(--lw-fg-secondary);
  }

  dd {
    margin: 0;
    font: var(--lw-type-body-sm);
  }
}

.lw-c-work-order__row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: var(--lw-space-3);
}

.lw-c-work-order__lines {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-3);
  margin: 0;
  padding: var(--lw-space-3);
  border: 1px solid var(--lw-line-hairline);
  border-radius: var(--lw-radius-sm);
}

.lw-c-work-order__legend,
.lw-c-work-order__heading {
  margin: 0;
  padding: 0;
  font: var(--lw-type-label);
  color: var(--lw-fg-primary);
}

.lw-c-work-order__line {
  display: grid;
  grid-template-columns: 7rem minmax(10rem, 2fr) 5rem 7rem 6rem auto;
  align-items: end;
  gap: var(--lw-space-2);

  @media (max-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
}

.lw-c-work-order__amount {
  margin: 0 0 var(--lw-space-2);
  font: var(--lw-type-body-sm);
  text-align: right;
}

.lw-c-work-order__error {
  grid-column: 1 / -1;
  margin: 0;
  font: var(--lw-type-caption);
  color: var(--lw-danger-fg);
}

.lw-c-work-order__lines-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--lw-space-3);
}

.lw-c-work-order__total {
  margin: 0;
  font: var(--lw-type-body);
  font-weight: 600;
}

.lw-c-work-order__lines-table {
  width: 100%;
  border-collapse: collapse;
  font: var(--lw-type-body-sm);

  th,
  td {
    padding: var(--lw-space-1) var(--lw-space-2);
    border-bottom: 1px solid var(--lw-line-hairline);
    text-align: left;
  }
}

.lw-c-work-order__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.lw-c-work-order__oos,
.lw-c-work-order__complete,
.lw-c-work-order__invoice,
.lw-c-work-order__defects {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-2);
}

.lw-c-work-order__check {
  display: flex;
  align-items: center;
  gap: var(--lw-space-2);
  font: var(--lw-type-body);
}

.lw-c-work-order__defect-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-2);
}

.lw-c-work-order__choice {
  display: flex;
  flex-direction: column;
  gap: var(--lw-space-1);
  margin: 0;
  padding: 0;
  border: 0;
}

.lw-c-work-order__statement {
  display: block;
  font: var(--lw-type-body-sm);
  font-style: italic;
  color: var(--lw-fg-secondary);
}

.lw-c-work-order__defect-area {
  display: block;
  font: var(--lw-type-body);
  font-weight: 600;
}

.lw-c-work-order__link {
  font: var(--lw-type-body-sm);
}

.lw-c-work-order__file {
  font: var(--lw-type-body-sm);
}
</style>
